/**
 * Auth & RBAC Zod schemas — Spec Bölüm 4.5
 */
import { z } from 'zod';
import {
  CuidSchema,
  EmailSchema,
  IndustrySchema,
  OrgRoleSchema,
  PasswordSchema,
} from './common.js';

// ─── Sign-up (Spec 4.5.4 + 1.5.T2) ───────────────────────────────
export const SignUpSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  name: z.string().min(2, 'İsim en az 2 karakter olmalı').max(100),
  // KVKK granular onay (zorunlu = AYDINLATMA)
  kvkkAydinlatmaAccept: z.literal(true, {
    errorMap: () => ({ message: 'Aydınlatma metnini onaylamanız gerekiyor' }),
  }),
  marketingConsent: z.boolean().optional().default(false),
});
export type SignUpInput = z.infer<typeof SignUpSchema>;

// ─── Sign-in (Spec 1.5.T3) ───────────────────────────────────────
export const SignInSchema = z.object({
  email: EmailSchema,
  password: z.string().min(1, 'Şifre gerekli'),
  rememberMe: z.boolean().optional().default(true),
});
export type SignInInput = z.infer<typeof SignInSchema>;

// ─── Email Verification (Spec 1.5.T2) ────────────────────────────
export const VerifyEmailSchema = z.object({
  email: EmailSchema,
  code: z.string().regex(/^\d{6}$/, '6 haneli kod girin'),
});
export type VerifyEmailInput = z.infer<typeof VerifyEmailSchema>;

// ─── Forgot Password (Spec 1.5.T4) ───────────────────────────────
export const ForgotPasswordSchema = z.object({
  email: EmailSchema,
});
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordSchema>;

export const ResetPasswordSchema = z.object({
  token: z.string().min(20),
  newPassword: PasswordSchema,
});
export type ResetPasswordInput = z.infer<typeof ResetPasswordSchema>;

// ─── Organization (Spec 1.5.T5) ──────────────────────────────────
export const CreateOrganizationSchema = z.object({
  name: z.string().min(2, 'Firma adı en az 2 karakter').max(200),
  industry: IndustrySchema,
  teamSize: z.number().int().min(1).max(10_000).optional(),
  kvkkAccept: z.literal(true, {
    errorMap: () => ({ message: 'KVKK onayı zorunludur' }),
  }),
  tosAccept: z.literal(true, {
    errorMap: () => ({ message: 'Kullanım şartlarını onaylamanız gerekiyor' }),
  }),
});
export type CreateOrganizationInput = z.infer<typeof CreateOrganizationSchema>;

export const UpdateOrganizationSchema = z.object({
  name: z.string().min(2).max(200).optional(),
  industry: IndustrySchema.optional(),
  teamSize: z.number().int().min(1).max(10_000).optional(),
  contactPhone: z.string().max(20).optional(),
});
export type UpdateOrganizationInput = z.infer<typeof UpdateOrganizationSchema>;

// ─── Invitations (Spec 1.5.T8) ───────────────────────────────────
export const InviteMemberSchema = z.object({
  email: EmailSchema,
  role: OrgRoleSchema.refine((r) => r !== 'OWNER', {
    message: 'Owner davetle eklenemez — devir akışı gerekli',
  }),
});
export type InviteMemberInput = z.infer<typeof InviteMemberSchema>;

export const AcceptInvitationSchema = z.object({
  token: z.string().min(20),
  // Yeni kullanıcı kayıt olacaksa
  password: PasswordSchema.optional(),
  name: z.string().min(2).max(100).optional(),
  kvkkAydinlatmaAccept: z.boolean().optional(),
});
export type AcceptInvitationInput = z.infer<typeof AcceptInvitationSchema>;

// ─── Member Management (Spec 1.5.T9) ─────────────────────────────
export const UpdateMemberRoleSchema = z.object({
  role: OrgRoleSchema.refine((r) => r !== 'OWNER', {
    message: 'Owner rolü transfer-ownership endpoint\'iyle değiştirilir',
  }),
});
export type UpdateMemberRoleInput = z.infer<typeof UpdateMemberRoleSchema>;

// ─── Owner Transfer (Spec 1.5.T10) ───────────────────────────────
export const InitiateOwnerTransferSchema = z.object({
  newOwnerMemberId: CuidSchema,
});
export type InitiateOwnerTransferInput = z.infer<typeof InitiateOwnerTransferSchema>;

export const ConfirmOwnerTransferSchema = z.object({
  newOwnerMemberId: CuidSchema,
  confirmCode: z.string().regex(/^\d{6}$/, '6 haneli onay kodu girin'),
});
export type ConfirmOwnerTransferInput = z.infer<typeof ConfirmOwnerTransferSchema>;

// ─── Switch Active Organization (Spec 1.5.T7) ────────────────────
export const SwitchOrganizationSchema = z.object({
  organizationId: CuidSchema,
});
export type SwitchOrganizationInput = z.infer<typeof SwitchOrganizationSchema>;
